import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { environment } from 'src/environments/environment';
import { IDSAuthService } from 'src/app/services/idsauth.service';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from 'src/app/modules/usermanagement/services/user.service';
import { S3UrlService } from 'src/app/shared/utilities/s3-url.service';
import { UserprofileComponent } from '../../../modules/usermanagement/components/manage/userprofile/userprofile.component';
import { ChangePasswordComponent } from '../../../modules/usermanagement/components/manage/change-password/change-password.component';
import { ViewUserProfileResponse } from 'src/app/modules/usermanagement/models/user.model';
import { UserProfileService } from 'src/app/modules/usermanagement/services/user-profile.service';
import { CustomToastrService } from 'src/app/services/toastr.service';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { Subscription } from 'rxjs';
import { Router, NavigationStart } from '@angular/router';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit, OnDestroy {
  constructor(
    public _idsAuthService: IDSAuthService,
    private dialog: MatDialog,
    private userService: UserService,
    private s3UrlService: S3UrlService,
    private userProfileService: UserProfileService,
    private customToastrService: CustomToastrService,
    private dashboardService: DashboardService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  environment = environment;
  currentTheme: 'light' | 'dark' = 'light';
  userProfile: any = null;
  userProfilePicture: string = 'assets/images/profile.jpg';
  userId: string = '';
  displayName: string = '';
  roleName: string = '';
  currentTime: string = '';
  timeInterval: any;
  iotRoleGuid: string = '';
  deviceStoreNumber: string = '';
  deviceStreetAddress: string = '';
  deviceCityStateZip: string = '';
  deviceLocationLogo: string = '';
  logoImage: string = '';
  favImage: string = '';
  private userProfileSubscription?: Subscription;
  private deviceLocationSubscription?: Subscription;
  private routerSubscription?: Subscription;


  ngOnInit(): void {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' || 'light';
    document.body.classList.add(`theme-${savedTheme}`);
    this.currentTheme = savedTheme;
    this.updateCurrentTime();
    this.timeInterval = setInterval(() => this.updateCurrentTime(), 1000);
    this.loadUserProfileFromStorage();

    // Subscribe to user profile changes
    this.userProfileSubscription = this.userProfileService.userProfile$.subscribe(profile => {
      if (profile) {
        this.updateDisplayPropertiesFromViewProfile(profile);
      }
    });

    // Subscribe to device location from dashboard service
    this.deviceLocationSubscription = this.dashboardService.deviceLocation$.subscribe(locationData => {
      if (locationData && (locationData.streetAddress || locationData.cityStateZip)) {
        const brandUrl = this.s3UrlService.getLogoUrl(
          'brand',
          locationData.locationLogo || ''
        );
        this.deviceStoreNumber = locationData.storeNumber || '';
        this.deviceStreetAddress = locationData.streetAddress || '';
        this.deviceCityStateZip = locationData.cityStateZip || '';
        this.deviceLocationLogo = brandUrl;

        this.cdr.detectChanges();
      }
    });

    // Monitor route navigation and clear location when leaving dashboard
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Close the mobile drawer after navigating (presentation only).
        this.closeMobileSidebar();
        // Check if navigating away from dashboard route
        if (!event.url.includes('dashboard')) {
          this.deviceStoreNumber = '';
          this.deviceStreetAddress = '';
          this.deviceCityStateZip = '';
          this.deviceLocationLogo = '';
        }
      }
    });
  }

  toggleTheme(): void {
    const body = document.body;
    const isDark = body.classList.contains('theme-dark');
    body.classList.remove(isDark ? 'theme-dark' : 'theme-light');
    body.classList.add(isDark ? 'theme-light' : 'theme-dark');
    this.currentTheme = isDark ? 'light' : 'dark';
    localStorage.setItem('theme', this.currentTheme);
  }


  addBodyClass(event: Event) {
    event.preventDefault(); // Optional: Prevent anchor default behavior
    // On tablet/mobile the hamburger controls the off-canvas drawer.
    // On desktop it keeps the existing icon-collapse behaviour.
    if (this.isMobileViewport()) {
      document.body.classList.toggle('sidebar-mobile-open');
    } else {
      document.body.classList.toggle('sidebar-close');
    }
    // Let charts / maps / grids recompute their size after the layout shifts.
    this.notifyLayoutResize();
  }

  /** Close the mobile drawer (used by backdrop click and after navigation). */
  closeMobileSidebar(): void {
    if (document.body.classList.contains('sidebar-mobile-open')) {
      document.body.classList.remove('sidebar-mobile-open');
      this.notifyLayoutResize();
    }
  }

  private isMobileViewport(): boolean {
    return window.innerWidth <= 1199;
  }

  /** When we cross back to desktop width, make sure the mobile drawer state is cleared. */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isMobileViewport()) {
      document.body.classList.remove('sidebar-mobile-open');
    }
  }

  /**
   * Nudge container-aware widgets (ECharts, AG Grid, maps, etc.) to relayout
   * after the shell layout changes. Presentation only - no data changes.
   */
  private notifyLayoutResize(): void {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 320);
  }

  ngOnDestroy(): void {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
    if (this.userProfileSubscription) {
      this.userProfileSubscription.unsubscribe();
    }
    if (this.deviceLocationSubscription) {
      this.deviceStoreNumber = '';
      this.deviceStreetAddress = '';
      this.deviceCityStateZip = '';
      this.deviceLocationLogo = '';
      this.deviceLocationSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }


  handleFavicon(): void {
    const isValidLogo =
      this.userProfile.clientLogo &&
      this.userProfile.clientLogo !== 'null';

    const isValidFav =
      this.userProfile.clientFavLogo &&
      this.userProfile.clientFavLogo !== 'null';

    // ✅ CASE 4: BOTH missing → direct default favicon
    if (!isValidLogo && !isValidFav) {
      this.favImage = 'assets/images/engenuity-favicon.svg';
      this.setBrowserFavicon('assets/images/engenuity-favicon.svg');
      return;
    }

    // ✅ CASE 3: favicon exists (independent of logo)
    if (isValidFav) {
      // Use the S3 URL directly instead of fetching it via HttpClient.
      // An <img> tag loads cross-origin images without triggering CORS,
      // whereas getImageAsBase64() uses an XHR request that S3 blocks.
      const favUrl = this.s3UrlService.getFavUrl(
        'client',
        this.userProfile.clientFavLogo
      );

      this.favImage = favUrl;
      this.setBrowserFavicon(favUrl);
      return;
    }

    // ✅ CASE 2: favicon missing but logo exists → use logo
    if (isValidLogo) {
      this.favImage = this.logoImage;
      this.setBrowserFavicon(this.logoImage);
      return;
    }

    // ✅ fallback safety (should not hit)
    this.favImage = 'assets/images/engenuity-favicon.svg';
    this.setBrowserFavicon('assets/images/engenuity-favicon.svg');
  }

  /**
   * Dynamically update the browser tab favicon
   */
  private setBrowserFavicon(href: string): void {
    let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  }

  loadUserProfileFromStorage(): void {
    this.userProfile = localStorage.getItem('userProfile')
      ? JSON.parse(localStorage.getItem('userProfile')!)
      : null;

    if (this.userProfile) {

      const isValidLogo =
        this.userProfile.clientLogo &&
        this.userProfile.clientLogo !== 'null';

      if (isValidLogo) {
        // Use the S3 URL directly for the <img> src (same approach as the
        // clients grid). This avoids the CORS error caused by the XHR-based
        // getImageAsBase64() call against the S3 bucket.
        const logoUrl = this.s3UrlService.getLogoUrl(
          'client',
          this.userProfile.clientLogo
        );

        this.logoImage = logoUrl;
        this.handleFavicon(); // ✅ ALWAYS call after logo ready
      } else {
        // 👉 default logo
        this.logoImage = this.environment.appLogoPath;

        // 👉 still call favicon
        this.handleFavicon();
      }

      this.updateDisplayPropertiesFromViewProfile(this.userProfile);

    } else {
      this.logoImage = this.environment.appLogoPath;
      this.favImage = 'assets/images/engenuity-favicon.svg';

      const email = this._idsAuthService.userDetails;
      if (email && this._idsAuthService.isAuthenticated()) {
        this.userService.getUserProfileByEmail(email).subscribe({
          next: (viewProfile: ViewUserProfileResponse) => {
            this.userProfileService.setUserProfile(viewProfile);
            
          },
          error: () => {
            // Silently ignore — defaults already applied above.
          }
        });
      }
    }
  }

  /**
   * Update display properties from view user profile data
   * @param viewProfile - View user profile response data
   */
  private updateDisplayPropertiesFromViewProfile(viewProfile: ViewUserProfileResponse): void {
    // Update userId
    this.userId = viewProfile.id || '';

    // Update display name
    this.displayName = viewProfile.displayName || 'User';

    // Update role name
    this.iotRoleGuid = viewProfile.iotRoleGuid || '';
    this.roleName = viewProfile.roleName || '';

    // Update profile picture if available
    if (viewProfile.profilePicture) {
      this.userProfilePicture = this.s3UrlService.getS3Url('user', 'profilePicture', viewProfile.profilePicture);
    }

    // Store updated user profile
    this.userProfile = viewProfile;

    // Update time display with new timezone
    this.updateCurrentTime();
  }

  updateCurrentTime(): void {
    const now = new Date();

    const timezoneUtcName = this.userProfile?.timezoneUtcName || '(UTC+00:00)';

    // Extract the UTC offset label from timezoneUtcName
    // Handles formats like "(UTC+08:00)", "(UTC-03:30)", or "(UTC) Coordinated Universal Time"
    let utcOffsetLabel = '(UTC+00:00)';

    // First try to extract explicit offset format: (UTC+HH:MM) or (UTC-HH:MM)
    const offsetMatch = timezoneUtcName.match(/\(UTC[+-]\d{2}:\d{2}\)/);
    if (offsetMatch) {
      utcOffsetLabel = offsetMatch[0];
    } else if (timezoneUtcName.includes('(UTC)')) {
      // Handle plain UTC format: "(UTC) Coordinated Universal Time" etc.
      utcOffsetLabel = '(UTC)';
    }

    // Parse the numeric offset
    let offsetHours = 0;
    let offsetMinutes = 0;

    const numericOffsetMatch = timezoneUtcName.match(/UTC([+-])(\d{2}):?(\d{2})?/);
    if (numericOffsetMatch) {
      const sign = numericOffsetMatch[1];
      const hours = parseInt(numericOffsetMatch[2], 10);
      const minutes = parseInt(numericOffsetMatch[3] || '0', 10);

      // Apply sign to both hours and minutes (e.g., UTC-03:30 means -3 hours AND -30 minutes)
      offsetHours = sign === '+' ? hours : -hours;
      offsetMinutes = sign === '+' ? minutes : -minutes;
    }
    // For plain UTC format like "(UTC) Coordinated Universal Time", offsets remain 0

    // Convert current UTC time to target timezone
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const targetTime = new Date(utc + (offsetHours * 60 + offsetMinutes) * 60000);

    // Format time and date (in target timezone)
    const timeString = targetTime
      .toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      .toLowerCase()
      .replace(' ', '');

    const dateString = targetTime.toLocaleString('en-US', {
      weekday: 'long',
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });

    // Final formatted output
    this.currentTime = `${utcOffsetLabel} ${timeString}\n${dateString}`;
  }

  openEditProfile(): void {
    if (this.userId) {
      this.userService.getUserProfileById(this.userId).subscribe({
        next: (userProfile) => {
          const dialogRef = this.dialog.open(UserprofileComponent, {
            width: '90%',
            maxWidth: '1200px',
            height: 'auto',
            maxHeight: '90vh',

            data: { userProfile: userProfile, userId: this.userId, iotRoleGuid: this.iotRoleGuid }
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result) {
              // Refresh view profile data after successful edit
              const email = this.userProfile?.emaicurrentUserTimeZonel;
              if (email) {
                this.userService.getUserProfileByEmail(email).subscribe({
                  next: (viewProfile: ViewUserProfileResponse) => {
                    // Update shared service with fresh profile data
                    this.userProfileService.setUserProfile(viewProfile);
                    this.updateDisplayPropertiesFromViewProfile(viewProfile);
                  },
                  error: (error) => {
                    console.error('Error fetching view profile:', error);
                  }
                });
              }
            }
          });
        },
        error: (error) => {
          console.error('Error fetching user profile:', error);
        }
      });
    }
  }

  openChangePassword(): void {
    // Try to get userId from multiple sources
    let userIdToUse = this.userId;

    // If userId is not set, try to get it from the user profile service
    if (!userIdToUse) {
      const userProfile = this.userProfileService.getCurrentUserProfile();
      if (userProfile?.id) {
        userIdToUse = userProfile.id;
        this.userId = userIdToUse; // Update the local property
      }
    }

    // If still no userId, try to extract from JWT token and fetch profile
    if (!userIdToUse) {
      const email = this.userProfile?.email;
      if (email) {
        this.userService.getUserProfileByEmail(email).subscribe({
          next: (viewProfile) => {
            localStorage.setItem('userProfile', JSON.stringify(viewProfile));
            this.updateDisplayPropertiesFromViewProfile(viewProfile);
            // Now open the dialog with the retrieved userId
            this.openChangePasswordDialog(viewProfile.id);
          },
          error: (error) => {
            console.error('Error fetching user profile for change password:', error);
            this.customToastrService.error('Could not retrieve user information. Please refresh the page and try again.', 'Error');
          }
        });
        return; // Exit here as the dialog will be opened in the success callback
      }
    }

    if (userIdToUse) {
      this.openChangePasswordDialog(userIdToUse);
    } else {
      console.error('No userId available for change password');
      this.customToastrService.error('User ID not found. Please refresh the page and try again.', 'Error');
    }
  }

  private openChangePasswordDialog(userId: string): void {
    const dialogRef = this.dialog.open(ChangePasswordComponent, {
      width: '600px',
      maxHeight: '90vh',
      data: { userId: userId }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Password changed successfully');
      }
    });
  }

  logout() {
    // Clear all local state synchronously BEFORE the browser redirects to IDS.
    // signoutRedirect() causes a full page unload so any .then() callbacks
    // after it never execute — localStorage must be cleared here first.
    this.userProfileService.clearUserProfile();
    localStorage.clear();
    this._idsAuthService.userDetails = null;
    // Redirect to IDS to clear the server-side session (cookie)
    this._idsAuthService.endSignoutMainWindow().catch((error) => {
      console.error('Error during IDS signout:', error);
      window.location.href = '/login';
    });
  }
}
